import React, { useState, useMemo } from 'react';
import { MessageCircle, ThumbsUp, Edit2, X, Send, Lightbulb, ExternalLink, MessageSquare, Bot } from 'lucide-react';
import { Button } from './ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import { cn } from '../lib/utils';
import { generateDraftResponse, generateDraftWithInstructions, getSourcesWithFeedback, getInitialDraftSources } from '../lib/assistant';
import { MessageSource } from '../types/chat';
import { useSystemNotes } from '../context/SystemNotesContext';

interface ReviewActionsProps {
  conversationId: string;
  question: string;
  mentionedPerson?: string;
  onApprove: (response: string) => void;
  onReject: () => void;
  onRequestClarification: (clarification: string) => void;
  className?: string;
}

export const ReviewActions: React.FC<ReviewActionsProps> = ({
  conversationId,
  question,
  mentionedPerson,
  onApprove,
  onReject,
  onRequestClarification,
  className,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draftResponse, setDraftResponse] = useState(() => generateDraftResponse(question, mentionedPerson));
  const [showClarificationForm, setShowClarificationForm] = useState(false);
  const [clarificationText, setClarificationText] = useState('');
  const [showInstructionsForm, setShowInstructionsForm] = useState(false);
  const [instructionsText, setInstructionsText] = useState('');
  const initialSources = useState<MessageSource[]>(() => getInitialDraftSources(question, mentionedPerson))[0];
  const [sources, setSources] = useState<MessageSource[]>(initialSources);
  const [primarySources, setPrimarySources] = useState<MessageSource[]>([]);
  const [hasInstructions, setHasInstructions] = useState(false);
  const [isGeneratingDraft, setIsGeneratingDraft] = useState(false);
  const { addSystemNote } = useSystemNotes();

  const handleApprove = () => {
    if (draftResponse.trim()) {
      onApprove(draftResponse);
    }
  };

  const handleRequestClarification = () => {
    if (clarificationText.trim()) {
      onRequestClarification(clarificationText);
      setClarificationText('');
      setShowClarificationForm(false);
    }
  };

  const handleAddInstructions = async () => {
    if (instructionsText.trim()) {
      setIsGeneratingDraft(true);
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 800));

      // Generate new draft with instructions
      const newDraft = generateDraftWithInstructions(question, draftResponse, instructionsText, mentionedPerson);
      setDraftResponse(newDraft);

      // Get sources with feedback incorporated (these are the primary/reviewer-selected sources)
      const newPrimarySources = getSourcesWithFeedback(question, instructionsText, mentionedPerson);
      setPrimarySources(newPrimarySources);

      // Keep initial sources as supporting context
      setSources(initialSources);
      setHasInstructions(true);

      // Save instruction as system note for future reference
      addSystemNote(instructionsText, question, mentionedPerson);

      // Reset and close form
      setInstructionsText('');
      setShowInstructionsForm(false);
      setIsGeneratingDraft(false);
    }
  };

  return (
    <div className={cn("bg-muted/30 p-4", className)}>
      <div className="max-w-4xl mx-auto space-y-4">

        {!showClarificationForm ? (
          <div className="space-y-4">
            {/* Question Section */}
            <div>
              <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                Question
              </div>
              <p className="text-sm font-medium text-foreground bg-muted/50 p-3 rounded-md">
                {question}
              </p>
            </div>

            {/* Draft Response Section */}
            <div>
              <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                Draft Response
              </div>
              {isEditing ? (
                <div className="space-y-3">
                  {!showInstructionsForm && (
                    <div className="text-xs text-muted-foreground bg-muted/50 p-2 rounded-md border border-border/50">
                      This draft was generated using a broad scan of available sources to avoid over-weighting any single document.
                    </div>
                  )}
                  {showInstructionsForm ? (
                    <div className="space-y-3">
                      <div>
                        <label className="text-sm font-medium mb-2 block">Refine with Additional Context (optional)</label>
                        <p className="text-xs text-muted-foreground mb-2">
                          Use this when a specific document or discussion should be prioritized for this response.
                        </p>
                        <textarea
                          value={instructionsText}
                          onChange={(e) => setInstructionsText(e.target.value)}
                          placeholder="e.g., Review the Q3/Q4 Planning Discussion in the #project-updates Teams channel and my recent rollout notes, or Add more context about Q3 targets..."
                          className="w-full min-h-[100px] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-none"
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={handleAddInstructions}
                          disabled={!instructionsText.trim() || isGeneratingDraft}
                          size="sm"
                          className="flex-1"
                        >
                          <Lightbulb className="h-4 w-4 mr-2" />
                          {isGeneratingDraft ? 'Generating...' : 'Generate New Draft'}
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => {
                            setShowInstructionsForm(false);
                            setInstructionsText('');
                          }}
                          size="sm"
                          disabled={isGeneratingDraft}
                        >
                          <X className="h-4 w-4 mr-2" />
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <textarea
                        value={draftResponse}
                        onChange={(e) => setDraftResponse(e.target.value)}
                        placeholder="Enter your draft response..."
                        className="w-full min-h-[120px] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-none"
                      />
                      {(sources.length > 0 || primarySources.length > 0) && (
                        <div className="mt-4 pt-4 border-t border-border/50">
                          {hasInstructions ? (
                            <div className="space-y-4">
                              {primarySources.length > 0 && (
                                <div>
                                  <p className="text-sm font-medium mb-3 text-foreground">Primary Context (Reviewer-selected)</p>
                                  <CategorizedSources sources={primarySources} showInitialContextLabel={false} />
                                </div>
                              )}
                              {sources.length > 0 && (
                                <div>
                                  <p className="text-sm font-medium mb-3 text-foreground">Supporting Context (High-level)</p>
                                  <CategorizedSources sources={sources} showInitialContextLabel={false} />
                                </div>
                              )}
                            </div>
                          ) : (
                            <>
                              <p className="text-sm font-medium mb-3 text-muted-foreground">Initial Context (High-level scan)</p>
                              <CategorizedSources sources={sources} showInitialContextLabel={true} />
                            </>
                          )}
                        </div>
                      )}
                      <div className="flex gap-2">
                        <Button
                          onClick={() => {
                            handleApprove();
                            setIsEditing(false);
                          }}
                          disabled={!draftResponse.trim()}
                          size="sm"
                          className="flex-1"
                        >
                          <Send className="h-4 w-4 mr-2" />
                          Send
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => setShowInstructionsForm(true)}
                          size="sm"
                          className="flex-1"
                        >
                          <Lightbulb className="h-4 w-4 mr-2" />
                          Add Instructions
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => {
                            setIsEditing(false);
                            setShowInstructionsForm(false);
                            setInstructionsText('');
                            setSources(initialSources);
                            setPrimarySources([]);
                            setHasInstructions(false);
                          }}
                          size="sm"
                          className="flex-1"
                        >
                          <X className="h-4 w-4 mr-2" />
                          Cancel
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="bg-background border border-border rounded-md p-3">
                    <p className="text-sm text-foreground whitespace-pre-wrap">
                      {draftResponse}
                    </p>
                  </div>
                  {!hasInstructions && (
                    <div className="text-xs text-muted-foreground bg-muted/50 p-2 rounded-md border border-border/50">
                      This draft was generated using a broad scan of available sources to avoid over-weighting any single document.
                    </div>
                  )}
                  {(sources.length > 0 || primarySources.length > 0) && (
                    <div className="mt-4 pt-4 border-t border-border/50">
                      {hasInstructions ? (
                        <div className="space-y-4">
                          {primarySources.length > 0 && (
                            <div>
                              <p className="text-sm font-medium mb-3 text-foreground">Primary Context (Reviewer-selected)</p>
                              <CategorizedSources sources={primarySources} showInitialContextLabel={false} />
                            </div>
                          )}
                          {sources.length > 0 && (
                            <div>
                              <p className="text-sm font-medium mb-3 text-foreground">Supporting Context (High-level)</p>
                              <CategorizedSources sources={sources} showInitialContextLabel={false} />
                            </div>
                          )}
                        </div>
                      ) : (
                        <>
                          <p className="text-sm font-medium mb-3 text-muted-foreground">Initial Context (High-level scan)</p>
                          <CategorizedSources sources={sources} showInitialContextLabel={true} />
                        </>
                      )}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Button
                      onClick={handleApprove}
                      size="sm"
                      className="flex-1"
                    >
                      <ThumbsUp className="h-4 w-4 mr-2" />
                      Approve
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setIsEditing(true)}
                      size="sm"
                      className="flex-1"
                    >
                      <Edit2 className="h-4 w-4 mr-2" />
                      Edit
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium mb-2 block">Clarification Request:</label>
              <textarea
                value={clarificationText}
                onChange={(e) => setClarificationText(e.target.value)}
                placeholder="What additional information do you need?"
                className="w-full min-h-[80px] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              />
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={handleRequestClarification}
                disabled={!clarificationText.trim()}
                size="sm"
              >
                <MessageCircle className="h-4 w-4 mr-2" />
                Request Clarification
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowClarificationForm(false);
                  setClarificationText('');
                }}
                size="sm"
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Categorize sources into groups (similar to ChatMessages)
type SourceCategory = 'knowledge-base' | 'communication' | 'assistants';

interface CategorizedSourcesProps {
  sources: MessageSource[];
  showInitialContextLabel?: boolean;
}

const CategorizedSources: React.FC<CategorizedSourcesProps> = ({ sources, showInitialContextLabel = false }) => {
  const categorized = useMemo(() => {
    const categories: Record<SourceCategory, MessageSource[]> = {
      'knowledge-base': [],
      'communication': [],
      'assistants': [],
    };

    sources.forEach(source => {
      if (source.type === 'sharepoint' || source.type === 'onedrive') {
        categories['knowledge-base'].push(source);
      } else if (source.type === 'teams' || source.type === 'email') {
        categories['communication'].push(source);
      } else if (source.type === 'human-assistant' || source.type === 'assistant') {
        categories['assistants'].push(source);
      } else {
        categories['knowledge-base'].push(source);
      }
    });

    return categories;
  }, [sources]);

  const getCategoryLabel = (category: SourceCategory): string => {
    switch (category) {
      case 'knowledge-base':
        return showInitialContextLabel ? 'Initial Context (High-level scan)' : 'Knowledge Base';
      case 'communication':
        return 'Communication Channels';
      case 'assistants':
        return 'Assistants';
      default:
        return 'Other';
    }
  };

  const hasAnySources = Object.values(categorized).some(sources => sources.length > 0);

  if (!hasAnySources) return null;

  return (
    <Accordion type="multiple" className="w-full">
      {categorized['knowledge-base'].length > 0 && (
        <AccordionItem value="knowledge-base" className="border-none">
          <AccordionTrigger className="py-2 text-xs font-medium text-muted-foreground hover:no-underline">
            {getCategoryLabel('knowledge-base')} ({categorized['knowledge-base'].length})
          </AccordionTrigger>
          <AccordionContent className="pb-2">
            <div className="flex flex-col gap-2">
              {categorized['knowledge-base'].map((source) => (
                <MessageSourceItem key={source.id} source={source} />
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>
      )}

      {categorized['communication'].length > 0 && (
        <AccordionItem value="communication" className="border-none">
          <AccordionTrigger className="py-2 text-xs font-medium text-muted-foreground hover:no-underline">
            {getCategoryLabel('communication')} ({categorized['communication'].length})
          </AccordionTrigger>
          <AccordionContent className="pb-2">
            <div className="flex flex-col gap-2">
              {categorized['communication'].map((source) => (
                <MessageSourceItem key={source.id} source={source} />
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>
      )}

      {categorized['assistants'].length > 0 && (
        <AccordionItem value="assistants" className="border-none">
          <AccordionTrigger className="py-2 text-xs font-medium text-muted-foreground hover:no-underline">
            {getCategoryLabel('assistants')} ({categorized['assistants'].length})
          </AccordionTrigger>
          <AccordionContent className="pb-2">
            <div className="flex flex-col gap-2">
              {categorized['assistants'].map((source) => (
                <MessageSourceItem key={source.id} source={source} />
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>
      )}
    </Accordion>
  );
};

// Logo components for different services
const SharePointLogo: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2L2 7l10 5 10-5-10-5z" fill="#0078D4" />
    <path d="M2 12l10 5 10-5" fill="#0078D4" opacity="0.85" />
    <path d="M2 17l10 5 10-5" fill="#0078D4" opacity="0.7" />
  </svg>
);

const TeamsLogo: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="4" y="6" width="14" height="11" rx="1" fill="#6264A7" />
    <rect x="18" y="4" width="4" height="15" rx="1" fill="#464EB8" />
    <circle cx="8" cy="11.5" r="1.5" fill="white" />
  </svg>
);

const OneDriveLogo: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12.5 7.5c-1.1 0-2 .9-2 2s.9 2 2 2h7c1.1 0 2-.9 2-2s-.9-2-2-2c-.2-1.1-1.1-2-2.2-2-1.1 0-2 .9-2.2 2H12.5z" fill="#0078D4" />
    <path d="M19.5 9.5c1.1 0 2 .9 2 2s-.9 2-2 2h-7c-1.1 0-2-.9-2-2s.9-2 2-2h7z" fill="#28A8EA" />
  </svg>
);

const EmailLogo: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
    <polyline points="22,6 12,13 2,6" />
  </svg>
);

// Source item component
const MessageSourceItem: React.FC<{ source: MessageSource }> = ({ source }) => {
  const getLogo = () => {
    const logoClass = "h-4 w-4 flex-shrink-0";
    switch (source.type) {
      case 'onedrive':
        return <OneDriveLogo className={logoClass} />;
      case 'sharepoint':
        return <SharePointLogo className={logoClass} />;
      case 'email':
        return <EmailLogo className={cn(logoClass, "text-blue-600 dark:text-blue-400")} />;
      case 'teams':
        return <TeamsLogo className={logoClass} />;
      case 'assistant':
        return <MessageSquare className={cn(logoClass, "text-purple-500 dark:text-purple-400")} />;
      case 'human-assistant':
        return <Bot className={cn(logoClass, "text-indigo-500 dark:text-indigo-400")} />;
      default:
        return <ExternalLink className={cn(logoClass, "text-muted-foreground")} />;
    }
  };

  const getServiceName = () => {
    switch (source.type) {
      case 'onedrive':
        return 'OneDrive';
      case 'sharepoint':
        return 'SharePoint';
      case 'email':
        return 'Email';
      case 'teams':
        return 'Teams';
      case 'assistant':
        return 'Assistant';
      case 'human-assistant':
        return "Human's Assistant";
      default:
        return null;
    }
  };

  const hasServiceLogo = ['onedrive', 'sharepoint', 'teams', 'email', 'assistant', 'human-assistant'].includes(source.type);
  const serviceName = getServiceName();

  return (
    <a
      href={source.url}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-start gap-3 p-2.5 rounded-md hover:bg-accent/50 transition-colors group"
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm font-medium text-foreground group-hover:text-primary">
            {source.title}
          </p>
          {hasServiceLogo && serviceName && (
            <div className="flex items-center gap-1.5 flex-shrink-0">
              {getLogo()}
              <span className="text-xs text-muted-foreground font-medium">
                {serviceName}
              </span>
            </div>
          )}
        </div>
        {source.description && (
          <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
            {source.description}
          </p>
        )}
      </div>
      <ExternalLink className="h-3.5 w-3.5 text-muted-foreground group-hover:text-foreground flex-shrink-0 mt-1" />
    </a>
  );
};
